import React, { useState, useEffect } from "react";
import { Box, Collapse, Divider, Typography, Grid } from "@mui/material";
import ResistanceGenesTable from "../../shared/components/ResistanceGenesTable";
import PredictedResistanceTable from "../../shared/components/PredictedResistanceTable";
import ReadMoreButton from "../../shared/components/ReadMoreButton";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../shared/api/apiClient";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import IconButton from "@mui/material/IconButton";
import LoadingSpinner from "../../shared/components/LoadingSpinner";

export default function AnalyzeResultsResistanceResfinder() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;

    const [resfinderData, setResFinderData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id || !token) return;

            setLoading(true);

            try {
                const params = new URLSearchParams({
                    study_id: id,
                    token,
                });

                const data = await apiRequest(
                    `resdinder.php?${params.toString()}`,
                    { method: "GET" }
                );

                if (!cancelled) {
                    setResFinderData(data?.samples ?? []);
                }
            } catch (e) {
                if (!cancelled) console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };

    }, [id, token]);
    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
            }}
        >
            {/* Title block */}
            <Box
                sx={{
                    width: "100%",
                    mb: 6,
                }}
            >
                <Typography variant="h4">
                    Resistance genes
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 28,
                            borderTopRightRadius: 999,
                            borderBottomRightRadius: 999,
                            background: "#20BC71",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="h5">
                        resfinder
                    </Typography>
                </Box>
            </Box>


            <Grid
                container
                spacing={4}
                sx={{
                    width: "100%",
                    mb: 10,
                    display: 'flex',
                    justifyContent: 'flex-start'
                }}
            >
                {/* Left column */}
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "column",
                            height: "100%",
                        }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1">
                                These results were generated by Resfinder.
                            </Typography>
                            <Typography variant="body1">
                                To read the documentation please visit the oficial website
                            </Typography>
                        </Box>

                        <ReadMoreButton href={"https://github.com/cadms/resfinder"} />
                    </Box>
                </Grid>

                {/* Right column */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                >
                    <Box
                        sx={{
                            background: "#000",
                            color: "#fff",
                            width: "100%",              // 👈 ocupa ancho disponible
                            maxWidth: 460,
                            minWidth: { xs: "auto", md: 400 }, // 👈 evita romper en mobile
                            minHeight: 144,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "left",
                                wordBreak: "break-word",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            }}
                        >
                            <Typography variant="body2">
                                {"python -m resfinder  --db_path_res ${params.db_res}"}
                            </Typography>
                            <Typography variant="body2">
                                {"-o resfinder_results_bacterial/${sample}"}
                            </Typography>
                            <Typography variant="body2">
                                {"-l 0.6 -t 0.8 --acquired  -ifa ${spades_scaffold_bacterial}"}
                            </Typography>
                            <Typography variant="body2">
                                {"-l_p 0.6 -t_p 0.8 - - point"}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Loading / Samples */}
            {loading ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        placeItems: "center",
                        mt: 6,
                        gap: 2,
                    }}
                >
                    <LoadingSpinner size={170} barWidth={18} barHeight={48} />
                    <Typography sx={{ fontSize: 40 }}>Processing files</Typography>
                </Box>
            ) : (
                resfinderData?.map((sample, key) => <ResFinderSample sample={sample} key={key} />)
            )}
        </Box>
    );
}

const ResFinderSample = ({ sample }) => {
    const [antimicrobialData, setAntimicrobialData] = useState([]);
    const [geneData, setGeneData] = useState([]);
    const [openSample, setOpenSample] = useState(false);

    // ✅ esto era useState por error
    useEffect(() => {
        setGeneData(sample?.tables?.by_gene?.rows ?? []);
        setAntimicrobialData(sample?.tables?.by_antimicrobial_class?.rows ?? []);
    }, [sample]);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Sample header */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    cursor: 'pointer'
                }}
                onClick={() => setOpenSample((v) => !v)}
            >
                <Box sx={{ width: '100%' }}>
                    <Typography
                        variant="h6"
                    >
                        Sample {sample?.sample_id}
                    </Typography>
                </Box>

                <Box sx={{ width: '10%', display: 'flex', flexDirection: 'row', justifyContent: 'end', }}>
                    <IconButton
                        sx={{
                            maxWidth: '50px',
                            width: "40px",
                            aspectRatio: '1/1',
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                        }}

                        aria-label={openSample ? "Collapse" : "Expand"}
                    >
                        {openSample ? (
                            <ExpandLessRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        ) : (
                            <ExpandMoreRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={openSample} timeout={240} unmountOnExit>
                <Divider sx={{ borderColor: "#20BC71" }} />

                {/* By gene */}
                <Box sx={{ width: "100%", py: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        By gene
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: "#20BC71", mb: 1 }} />

                <Box sx={{ pb: 3 }}>
                    <ResistanceGenesTable rows={geneData} />
                </Box>


                {/* By antimicrobial class */}
                <Box sx={{ width: "100%", py: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        By antimicrobial class
                    </Typography>
                </Box>

                <Divider sx={{ borderColor: "#20BC71", mb: 2 }} />

                <Box sx={{ width: "100%", maxWidth: 760, mb: 1 }}>
                    <Typography variant="body1">
                        The phenotype "No resistance" should be interpreted with caution, as it only means that
                        nothing in the used database indicate resistance, but resistance could exist from
                        "unknown" or not yet implemented sources.
                    </Typography>

                    <Typography variant="body1">
                        The "Match" column stores one of the integers 0, 1, 2, 3.
                    </Typography>

                    <Box variant="body1">
                        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                            <li>0: No match found</li>
                            <li>1: Match &lt; 100% ID AND match length &lt; ref length</li>
                            <li>2: Match = 100% ID AND match length &lt; ref length</li>
                            <li>3: Match = 100% ID AND match length = ref length</li>
                        </ul>
                    </Box>

                    <Typography variant="body1">
                        If several hits causing the same resistance are found, the highest number will be stored
                        in the "Match" column.
                    </Typography>
                </Box>

                <Box sx={{ pb: 3, }}>
                    <PredictedResistanceTable rows={antimicrobialData} />
                </Box>
            </Collapse>

            <Divider sx={{ borderColor: "#20BC71" }} />
        </Box>
    );
};